/* One dialog for everything you can put on a day.
 *
 * There were three ways to add something and they were in three places: a
 * block went in from the day view, a ritual only existed in Settings, and a
 * birthday could only be added from the year. Which meant the answer to "how
 * do I add the gym every Tuesday" depended on knowing which of three screens
 * the app files that under — and nobody knows that, because it is a fact about
 * how this codebase stores things rather than about a week.
 *
 * So: one dialog, and the only question it asks first is **how often**.
 *
 *   · **Once** — a block on that day. Pinned, so the planner works around it.
 *   · **Every week** — a ritual. The hours already spoken for, held back
 *     before the week is offered to work.
 *   · **Every year** — a birthday, an anniversary, the day somebody died.
 *     Stored as a month and a day rather than a date, because a date that
 *     repeats for ever does not have a year.
 *
 * The date you opened it on carries into all three, so adding a birthday from
 * the 14th does not then ask you which day you meant.
 *
 * A **once** entry becomes an ordinary block — unless you open "travel,
 * getting ready and reminders", in which case it becomes an *appointment*: the
 * travel either side as real time on the day, the getting-ready as a task due
 * that day, and warnings early enough to be useful.
 *
 * A **once** block also asks what should happen to whatever is already there
 * — only then, push the rest later, the next free time, or fit the gap — and
 * who may see it. The answer is previewed before anything is written: "moves
 * Draft to 10:30" is said while you can still change your mind.
 */

import Icon from './Icon';
import type { IconName } from './Icon';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { OccasionKind } from '@shared/types';
import { post, errorMessage } from '../api';
import Dialog from './Dialog';
import { hhmm, toMinuteOfDay, friendlyDate, human } from './time';

type Repeat = 'once' | 'weekly' | 'yearly';
export type Clash = 'exact' | 'push' | 'next' | 'fit';
type Privacy = '' | 'busy' | 'hidden';

const REPEATS: { value: Repeat; label: string; hint: string }[] = [
  { value: 'once', label: 'Once', hint: 'On this day only. Pinned, so the planner works around it.' },
  { value: 'weekly', label: 'Every week', hint: 'A ritual: held every week before your time is offered to work.' },
  { value: 'yearly', label: 'Every year', hint: 'A birthday or anniversary — a month and a day, with no year.' }
];

export const CLASHES: { value: Clash; label: string; icon: IconName; hint: string }[] = [
  { value: 'exact', label: 'Only then', icon: 'lock',
    hint: 'At that time or not at all. Nothing else moves.' },
  { value: 'push', label: 'Push the rest', icon: 'right',
    hint: 'Whatever can move shifts later, just far enough. Meetings and pinned things never move.' },
  { value: 'next', label: 'Next free time', icon: 'search',
    hint: 'The first free stretch long enough, at or after that time.' },
  { value: 'fit', label: 'Fit the gap', icon: 'filter',
    hint: 'Starts then, and ends when the next thing starts.' }
];

const PRIVACY: { value: Privacy; label: string }[] = [
  { value: '', label: 'What its category says' },
  { value: 'busy', label: 'Only that I am busy' },
  { value: 'hidden', label: 'Nobody' }
];

const KINDS: { value: string; label: string }[] = [
  { value: 'focus', label: 'Work' },
  { value: 'event', label: 'Meeting' },
  { value: 'ritual', label: 'Yours' },
  { value: 'break', label: 'Break' },
  { value: 'travel', label: 'Travel' },
  { value: 'reward', label: 'Something for you' }
];

const OCCASION_KINDS: { value: OccasionKind; label: string }[] = [
  { value: 'birthday', label: 'A birthday' },
  { value: 'anniversary', label: 'An anniversary' },
  { value: 'memorial', label: 'A day to remember someone' },
  { value: 'other', label: 'Something else' }
];

const WEEK = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' }
];

const LEADS = [
  { days: 30, label: 'a month before' },
  { days: 7, label: 'a week' },
  { days: 1, label: 'the day before' },
  { days: 0, label: 'on the day' }
];

interface Moved { title: string; from: string; to: string }
type Preview =
  | { fits: boolean; said: string | null; start: string; end: string; moved: Moved[] }
  | { refused: string };

export default function AddToCalendar({ date, startMin = 9 * 60, onClose, onAdded }: {
  /** The day it was opened on. Everything is prefilled from it. */
  date: string;
  startMin?: number;
  onClose: () => void;
  /** `moved` is how many other things were pushed later to make room. */
  onAdded: (what: string, repeat: Repeat, moved?: number) => void;
}) {
  const [repeat, setRepeat] = useState<Repeat>('once');
  const [title, setTitle] = useState('');
  const [start, setStart] = useState(hhmm(Math.round(startMin / 15) * 15));
  const [minutes, setMinutes] = useState('60');
  const [kind, setKind] = useState('focus');
  const [error, setError] = useState<string | null>(null);

  /* once: what happens to what is there, and who sees it */
  const [clash, setClash] = useState<Clash>('exact');
  const [privacy, setPrivacy] = useState<Privacy>('');
  const [pinned, setPinned] = useState(true);
  const [preview, setPreview] = useState<Preview | null>(null);

  /* weekly */
  const weekdayOfDate = new Date(date + 'T12:00:00Z').getUTCDay();
  const [days, setDays] = useState<number[]>([weekdayOfDate]);
  const [protection, setProtection] = useState<'hard' | 'soft'>('soft');

  /* the appointment half of "once" */
  const [more, setMore] = useState(false);
  const [location, setLocation] = useState('');
  const [travelTo, setTravelTo] = useState('0');
  const [travelBack, setTravelBack] = useState('0');
  const [prep, setPrep] = useState('0');
  const [alarms, setAlarms] = useState<number[]>([60]);

  /* yearly — taken from the day this was opened on, which is the whole point
     of opening it from a calendar cell. */
  const [occasionKind, setOccasionKind] = useState<OccasionKind>('birthday');
  const [sinceYear, setSinceYear] = useState('');
  const [leadDays, setLeadDays] = useState<number[]>([7, 1]);
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));

  const mins = Math.max(5, Math.min(1440, Number(minutes) || 60));

  /* An appointment rather than a block, the moment anything around it has
     been filled in: travel, preparation and warnings are exactly what a block
     cannot carry. */
  const isAppointment = repeat === 'once' && more &&
    (Number(travelTo) > 0 || Number(travelBack) > 0 || Number(prep) > 0 ||
     location.trim() !== '' || alarms.length > 0);
  const asBlock = repeat === 'once' && !isAppointment;

  /* Ask the server what would happen, a beat after the last change. The same
     rules that will decide the real insert answer the preview, so the two
     cannot disagree. */
  useEffect(() => {
    if (!asBlock || !/^\d{2}:\d{2}$/.test(start)) { setPreview(null); return; }
    let live = true;
    const t = setTimeout(() => {
      void post<Preview & { preview: true }>('/api/plan/block', {
        date, startMin: toMinuteOfDay(start), minutes: mins,
        title: title.trim() || 'New', kind, mode: clash, dryRun: true
      }).then(r => {
        if (!live) return;
        if (!r.ok) setPreview({ refused: errorMessage(r, 'That will not fit.') });
        /* Only an answer that is actually a preview is shown — an older server
           that ignores dryRun would answer with a saved plan instead. */
        else if (typeof (r as { start?: unknown }).start === 'string') {
          const p = r as unknown as Preview & { moved?: Moved[] };
          setPreview({ ...p, moved: p.moved ?? [] } as Preview);
        } else setPreview(null);
      });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [asBlock, date, start, mins, title, kind, clash]);

  const save = useMutation({
    mutationFn: async (): Promise<number> => {
      const clean = title.trim();

      if (repeat === 'once') {
        if (isAppointment) {
          const r = await post('/api/appointments', {
            title: clean, date, startMin: toMinuteOfDay(start), minutes: mins,
            location: location.trim() || null,
            travelToMin: Math.max(0, Number(travelTo) || 0),
            travelBackMin: Math.max(0, Number(travelBack) || 0),
            prepMin: Math.max(0, Number(prep) || 0),
            alarms
          });
          if (!r.ok) throw new Error(errorMessage(r, 'Could not put it there.'));
          return 0;
        }

        const r = await post<{ moved?: Moved[] }>('/api/plan/block', {
          date, startMin: toMinuteOfDay(start), minutes: mins, title: clean, kind,
          mode: clash, privacy: privacy || null, locked: pinned
        });
        if (!r.ok) throw new Error(errorMessage(r, 'Could not put it there.'));
        return r.moved?.length ?? 0;
      }

      if (repeat === 'weekly') {
        if (!days.length) throw new Error('Which days?');
        const from = toMinuteOfDay(start);
        const r = await post('/api/rituals', {
          title: clean, startMin: from, endMin: Math.min(1440, from + mins),
          days, protection, kind: kind === 'break' ? 'break' : 'ritual'
        });
        if (!r.ok) throw new Error(errorMessage(r, 'Could not hold that time.'));
        return 0;
      }

      const r = await post('/api/occasions', {
        title: clean, kind: occasionKind, month, day,
        sinceYear: sinceYear.trim() ? Number(sinceYear) : null,
        leadDays
      });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return 0;
    },
    onSuccess: moved => { onAdded(title.trim(), repeat, moved); onClose(); },
    onError: (e: Error) => setError(e.message)
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('What is it?'); return; }
    setError(null);
    save.mutate();
  }

  const toggle = (list: number[], v: number, set: (n: number[]) => void) =>
    set(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  const chosen = REPEATS.find(r => r.value === repeat)!;
  const chosenClash = CLASHES.find(c => c.value === clash)!;

  return (
    <Dialog title="Add to your calendar" icon="calendar"
            description={`On ${friendlyDate(date)}. ${chosen.hint}`}
            onClose={onClose}>
      <form className="when-form" onSubmit={submit}>
        <div className="field field-wide">
          <label htmlFor="add-title">What is it?</label>
          <input id="add-title" value={title} maxLength={200} autoFocus
                 placeholder={repeat === 'yearly' ? "Dana's birthday"
                   : repeat === 'weekly' ? 'The gym' : 'Pick the kids up'}
                 onChange={e => setTitle(e.target.value)} />
        </div>

        {/* How often, asked first, because it changes what the rest means. */}
        <fieldset className="field-wide repeat-picker">
          <legend>How often</legend>
          {REPEATS.map(r => (
            <label key={r.value} className={`chip chip-pick${repeat === r.value ? ' is-on' : ''}`}>
              <input type="radio" name="repeat" value={r.value}
                     checked={repeat === r.value}
                     onChange={() => setRepeat(r.value)} />
              <Icon name={r.value === 'weekly' ? 'repeat' : r.value === 'yearly' ? 'cake' : 'calendar'} size={14} />
              {r.label}
            </label>
          ))}
        </fieldset>

        {repeat !== 'yearly' && (
          <>
            <div className="field">
              <label htmlFor="add-start">Starts at</label>
              <input id="add-start" type="time" value={start}
                     onChange={e => setStart(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="add-mins">Minutes</label>
              <input id="add-mins" type="number" min="5" max="1440" step="5" value={minutes}
                     onChange={e => setMinutes(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="add-kind">What kind of thing</label>
              <select id="add-kind" value={kind} onChange={e => setKind(e.target.value)}>
                {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
          </>
        )}

        {asBlock && (
          <>
            <fieldset className="field-wide repeat-picker clash-picker">
              <legend>If something is already there</legend>
              {CLASHES.map(c => (
                <label key={c.value} className={`chip chip-pick${clash === c.value ? ' is-on' : ''}`}
                       title={c.hint}>
                  <input type="radio" name="clash" value={c.value}
                         checked={clash === c.value}
                         onChange={() => setClash(c.value)} />
                  <Icon name={c.icon} size={14} />
                  {c.label}
                </label>
              ))}
              <p className="muted small">{chosenClash.hint}</p>
            </fieldset>

            <div className="field">
              <label htmlFor="add-privacy">Who can see it</label>
              <select id="add-privacy" value={privacy}
                      onChange={e => setPrivacy(e.target.value as Privacy)}>
                {PRIVACY.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <label className="check-field field-wide">
              <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
              <span><Icon name="pin" size={14} />Pin it, so a replan never moves it</span>
            </label>
          </>
        )}

        {repeat === 'once' && !more && (
          <button type="button" className="linkish field-wide" onClick={() => setMore(true)}>
            <Icon name="travel" size={15} />Travel, getting ready and reminders
          </button>
        )}

        {repeat === 'once' && more && (
          <>
            <div className="field field-wide">
              <label htmlFor="add-where">Where</label>
              <input id="add-where" value={location} maxLength={120}
                     placeholder="The Comedy Cellar"
                     onChange={e => setLocation(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="add-travel-to">Travel there (min)</label>
              <input id="add-travel-to" type="number" min="0" max="480" step="5" value={travelTo}
                     onChange={e => setTravelTo(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="add-travel-back">Travel back (min)</label>
              <input id="add-travel-back" type="number" min="0" max="480" step="5" value={travelBack}
                     onChange={e => setTravelBack(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="add-prep">Time to get ready (min)</label>
              <input id="add-prep" type="number" min="0" max="600" step="15" value={prep}
                     onChange={e => setPrep(e.target.value)} />
            </div>
            {Number(travelTo) > 0 && (
              <p className="muted small field-wide">
                You leave at {hhmm(Math.max(0, toMinuteOfDay(start) - (Number(travelTo) || 0)))},
                and nothing else can be planned from then.
              </p>
            )}
            <fieldset className="field-wide alarm-picker">
              <legend>Remind me</legend>
              {[1440, 120, 60, 30, 15].map(m => (
                <label key={m} className={`chip chip-pick${alarms.includes(m) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={alarms.includes(m)}
                         onChange={() => toggle(alarms, m, setAlarms)} />
                  {m === 1440 ? 'the day before' : m >= 60 ? `${m / 60}h` : `${m} min`}
                </label>
              ))}
            </fieldset>
          </>
        )}

        {repeat === 'once' && (!asBlock || !preview) && (
          <p className="muted small field-wide">
            {friendlyDate(date)}, {start}–{hhmm(Math.min(1439, toMinuteOfDay(start) + mins))}.
          </p>
        )}

        {/* What will actually happen, from the server's own rules. */}
        {asBlock && preview && (
          <div className={`insert-preview field-wide${'refused' in preview || !preview.fits ? ' is-bad' : ''}`}
               role="status" aria-live="polite">
            {'refused' in preview ? (
              <p><Icon name="info" size={14} />{preview.refused}</p>
            ) : (
              <>
                <p>
                  <Icon name={preview.fits ? 'check' : 'info'} size={14} />
                  {friendlyDate(date)}, <strong>{preview.start}–{preview.end}</strong>
                  {preview.start !== start && ' — the first time that works'}
                </p>
                {!preview.fits && preview.said && <p>{preview.said}</p>}
                {preview.moved.length > 0 && (
                  <ul className="insert-moved">
                    {preview.moved.map(m => (
                      <li key={m.title + m.from}>
                        <Icon name="right" size={12} />
                        <span>{m.title}</span>
                        <span className="muted">{m.from} → {m.to}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {repeat === 'weekly' && (
          <>
            <fieldset className="field-wide alarm-picker">
              <legend>Which days</legend>
              {WEEK.map(d => (
                <label key={d.value} className={`chip chip-pick${days.includes(d.value) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={days.includes(d.value)}
                         onChange={() => toggle(days, d.value, setDays)} />
                  {d.label}
                </label>
              ))}
            </fieldset>
            <div className="field field-wide">
              <label htmlFor="add-protection">If something clashes</label>
              <select id="add-protection" value={protection}
                      onChange={e => setProtection(e.target.value as 'hard' | 'soft')}>
                <option value="soft">It can give way — tell me when it does</option>
                <option value="hard">Never move it</option>
              </select>
            </div>
            <p className="muted small field-wide">
              {human(mins)} every {days.length === 7 ? 'day'
                : WEEK.filter(d => days.includes(d.value)).map(d => d.label).join(', ')},
              held before your week is offered to work.
            </p>
          </>
        )}

        {repeat === 'yearly' && (
          <>
            <div className="field">
              <label htmlFor="add-occasion-kind">What kind</label>
              <select id="add-occasion-kind" value={occasionKind}
                      onChange={e => setOccasionKind(e.target.value as OccasionKind)}>
                {OCCASION_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="add-since">
                {occasionKind === 'birthday' ? 'Born in' : 'Since'} <span className="muted">(optional)</span>
              </label>
              <input id="add-since" type="number" min="1900" max={new Date().getFullYear()}
                     value={sinceYear} placeholder="1994"
                     onChange={e => setSinceYear(e.target.value)} />
            </div>
            <fieldset className="field-wide alarm-picker">
              <legend>Tell me</legend>
              {LEADS.map(l => (
                <label key={l.days} className={`chip chip-pick${leadDays.includes(l.days) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={leadDays.includes(l.days)}
                         onChange={() => toggle(leadDays, l.days, setLeadDays)} />
                  {l.label}
                </label>
              ))}
            </fieldset>
            <p className="muted small field-wide">
              Every {new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' })
                .format(new Date(date + 'T12:00:00Z'))}, for ever — no year, so it never needs
              adding again.
            </p>
          </>
        )}

        {error && <p className="auth-err field-wide" role="alert">{error}</p>}

        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…'
              : repeat === 'weekly' ? 'Hold this time'
              : repeat === 'yearly' ? 'Add it' : 'Put it on the day'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}
